/*
 * ⚠️ 이 파일은 **두 벌 있다.**
 *     tastee_website/src/lib/quizScoring.ts   (tastee.kr/quiz — 계정 없는 사람)
 *     tastee_web-main/src/lib/quizScoring.ts  (앱 안 환경설정 > 취향분석)
 *
 * 문항이나 점수 계산을 고칠 때 **반드시 양쪽을 같이** 고친다. 한쪽만 고치면
 * 같은 테스트가 다른 결과를 내는데, 오류가 안 나서 아무도 모른다.
 * 저장소가 달라 공유할 방법이 마땅치 않아 이렇게 둔다.
 *
 * 보정값(quizCalibration.ts)은 웹사이트 쪽에서
 *     node scripts/calibrate-quiz.mjs
 * 로 만든 뒤 이쪽으로 복사한다. 점수를 고쳤으면 **반드시 다시 만든다.**
 *
 * 한 군데만 다르다 — import 경로의 '.ts' 확장자를 뗐다. 웹사이트는 Node
 * 스크립트가 직접 읽느라 붙여 두는데, 이쪽 빌드(CRA)는 그걸 거절한다.
 */
/**
 * 입맛 유형 테스트 v2 — 점수 계산 (순수 함수, 화면 의존 없음).
 *
 * 1. 원점수: 고른 답의 점수를 성향별로 합산 (음수 포함)
 * 2. z-score: (원점수 − 평균) / 표준편차   ← 성향마다 문항 수·점수 폭이 달라 생기는 쏠림을 없앤다
 * 3. 보정: z + offset                     ← 무작위 응답 기준으로 8유형이 고르게 나오도록 맞춘 값
 * 4. 1위·2위 성향 = 보정 점수 상위 2개 (동점 규칙은 pickOrder 참고)
 *
 * 평균·표준편차·보정값은 scripts/calibrate-quiz.mjs가 만든 quizCalibration.ts에서 읽는다.
 * 이 파일은 Node 스크립트에서도 직접 import하므로 별칭 import(@/…)를 쓰지 않는다.
 */
import { CHIP, QUESTIONS, TIE_ORDER, TRAITS, type Points, type Trait } from "../data/quiz";
import { CALIBRATION, type Calibration } from "../data/quizCalibration";

/** 문항 ID(q1, q2f, chip_freq …) → 답 ID(a, b …) */
export type Selections = Record<string, string>;

export type TraitScores = Record<Trait, number>;

export interface QuizResult {
  primary: Trait;
  secondary: Trait;
  raw: TraitScores;
  adjusted: TraitScores;
  /** 레이더 차트용 0~100 (무작위 응답 분포 기준 백분위) */
  radar: TraitScores;
  insights: Record<string, string>;
  scoringVersion: string;
}

/*
 * 1·2위를 가를 때 "사실상 같다"고 볼 차이.
 *
 * 1e-9 였을 때 실제로 난 일(2026-10-29) — 어떤 사람의 원점수가
 * 단골 5 · 가성비 4 였는데, 보정값이 1.146973 대 1.146215 로 갈려
 * **원점수가 낮은 가성비가 1위**가 됐다. 본인은 단골이라고 느꼈다.
 *
 * 보정은 "남들과 비교해 얼마나 유별난가"를 재는 값이라 성향마다 눈금이
 * 다르다. 눈금 차이가 소수점 넷째 자리까지 내려가면 그건 신호가 아니라
 * 잡음이다. 그 정도로 붙으면 원점수로 가르는 편이 사람 느낌에 가깝다.
 */
const EPS = 0.05;

const zeroScores = (): TraitScores =>
  Object.fromEntries(TRAITS.map((t) => [t, 0])) as TraitScores;

const addPoints = (target: TraitScores, points?: Points) => {
  if (!points) return;
  for (const [t, v] of Object.entries(points)) target[t as Trait] += v ?? 0;
};

export function rawScores(sel: Selections): TraitScores {
  const raw = zeroScores();
  for (const q of QUESTIONS) {
    const ans = q.answers.find((a) => a.id === sel[q.id]);
    addPoints(raw, ans?.points);
  }
  return raw;
}

/** q12 답이 가리키는 성향들 (동점 세 번째 규칙) */
function q12Traits(sel: Selections): Trait[] {
  const q12 = QUESTIONS.find((q) => q.id === "q12");
  const ans = q12?.answers.find((a) => a.id === sel.q12);
  return ans?.points ? (Object.keys(ans.points) as Trait[]) : [];
}

/**
 * 보정 점수 내림차순 정렬. 동점이면
 * 원점수가 큰 쪽 → q12 답의 성향 → explorer, regular, value, experience, solo, mood, trend, waiting 순.
 */
export function pickOrder(adjusted: TraitScores, raw: TraitScores, q12: Trait[]): Trait[] {
  return [...TRAITS].sort((a, b) => {
    const d = adjusted[b] - adjusted[a];
    if (Math.abs(d) > EPS) return d;
    if (raw[b] !== raw[a]) return raw[b] - raw[a];
    const qa = q12.includes(a) ? 1 : 0;
    const qb = q12.includes(b) ? 1 : 0;
    if (qa !== qb) return qb - qa;
    return TIE_ORDER.indexOf(a) - TIE_ORDER.indexOf(b);
  });
}

/** 정렬된 분위수 표(0~100%)에서 값의 백분위 */
export function percentile(value: number, quantiles: number[]): number {
  const n = quantiles.length;
  if (!n) return 50;
  if (value <= quantiles[0]) return 0;
  if (value >= quantiles[n - 1]) return 100;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (quantiles[mid] <= value) lo = mid;
    else hi = mid;
  }
  const span = quantiles[hi] - quantiles[lo] || 1;
  return ((lo + (value - quantiles[lo]) / span) / (n - 1)) * 100;
}

export function collectInsights(sel: Selections): Record<string, string> {
  const out: Record<string, string> = {};
  const take = (id: string, key: string | undefined, answers: { id: string; insight?: string }[]) => {
    if (!key) return;
    const a = answers.find((x) => x.id === sel[id]);
    if (a?.insight) out[key] = a.insight;
  };
  for (const q of QUESTIONS) {
    take(q.id, q.insightKey, q.answers);
    if (q.followUp) take(q.followUp.id, q.followUp.insightKey, q.followUp.answers);
  }
  take(CHIP.id, CHIP.insightKey, CHIP.answers);
  return out;
}

export function scoreWith(cal: Calibration, sel: Selections): QuizResult {
  const raw = rawScores(sel);
  const adjusted = zeroScores();
  const radar = zeroScores();
  for (const t of TRAITS) {
    const sd = cal.sd[t] || 1;
    adjusted[t] = (raw[t] - cal.mean[t]) / sd + cal.offset[t];
    radar[t] = Math.round(percentile(adjusted[t], cal.quantiles[t] ?? []));
  }
  const order = pickOrder(adjusted, raw, q12Traits(sel));
  return {
    primary: order[0],
    secondary: order[1],
    raw,
    adjusted,
    radar,
    insights: collectInsights(sel),
    scoringVersion: cal.version,
  };
}

export const score = (sel: Selections): QuizResult => scoreWith(CALIBRATION, sel);

/** 모든 메인 문항과 펼쳐진 보조 질문, 칩까지 답했는지 */
export function isComplete(sel: Selections): boolean {
  for (const q of QUESTIONS) {
    if (!sel[q.id]) return false;
    if (q.followUp && !sel[q.followUp.id]) return false;
  }
  return Boolean(sel[CHIP.id]);
}

/** DB 저장용: 문항 순서대로 [{q, a}] */
export function toAnswerList(sel: Selections): { q: string; a: string }[] {
  const ids: string[] = [];
  for (const q of QUESTIONS) {
    ids.push(q.id);
    if (q.followUp) ids.push(q.followUp.id);
  }
  ids.push(CHIP.id);
  return ids.filter((id) => sel[id]).map((id) => ({ q: id, a: sel[id] }));
}
